import { SelectedPurse } from '@/storage/applications/providerPaySlice'

export function getTotalAllocated(selectedPurses: { amount: number }[]) {
  let current = 0

  selectedPurses.forEach((purse) => {
    current += Number(purse.amount)
  })

  return parseFloat(current.toFixed(2))
}

export function roundToTwoDecimals(value: number) {
  return parseFloat(value.toFixed(2))
}

export function calculateWithServiceFee(
  baseAmount: number,
  feePercentage: number
) {
  // Convert to cents to avoid floating point issues
  const baseAmountCents = Math.round(baseAmount * 100)

  const feeAmountCents = Math.round(baseAmountCents * (feePercentage / 100))

  // Calculate total in cents
  const totalAmountCents = baseAmountCents + feeAmountCents

  // might look a bit stupid to calculate this right now.
  // But I think this will be beneficial in the future when the rules for service fee changes.
  const totalMinusServiceFeeCents = totalAmountCents - feeAmountCents

  // Convert back to dollars for display
  return {
    baseAmount: baseAmount,
    feePercentage: feePercentage,
    feeAmount: feeAmountCents / 100,
    totalAmount: totalAmountCents / 100,
    // Also return the cent values for backend processing
    baseAmountCents: baseAmountCents,

    feeAmountCents: feeAmountCents,
    totalAmountCents: totalAmountCents,
    totalMinusServiceFeeCents: totalMinusServiceFeeCents,
    totalMinusServiceFee: totalMinusServiceFeeCents / 100,
  }
}

/**
 * The shape of a `purse_movements` row on a payment detail. Only the fields we
 * need to rebuild an allocation are required, so any richer row fits as well.
 */
export type PaymentPurseMovement = {
  purse_id: number
  amount: number
}

export type PurseAllocationEntry = {
  purse_id: number
  amount: number
}

type PurseWithBalance = {
  id: number
  name: string
  balance_available: number
}

/**
 * Collapses the `purse_movements` rows of a payment into one entry per wallet.
 * A payment can hold several rows against the same wallet, and everything we do
 * with an existing allocation (pre-filling, comparing, adding the hold back to
 * the balance) works on the per wallet total.
 */
export function aggregatePurseMovements(
  purseMovements: PaymentPurseMovement[]
): PurseAllocationEntry[] {
  const totalsByPurseId = new Map<number, number>()

  purseMovements.forEach((movement) => {
    const current = totalsByPurseId.get(movement.purse_id) ?? 0
    totalsByPurseId.set(movement.purse_id, current + Number(movement.amount))
  })

  return Array.from(totalsByPurseId, ([purse_id, amount]) => ({
    purse_id,
    amount: roundToTwoDecimals(amount),
  }))
}

/**
 * How much of a wallet is already held by this very payment.
 */
export function getHoldForThisPayment(
  purseId: number,
  purseMovements: PaymentPurseMovement[]
) {
  return roundToTwoDecimals(
    purseMovements
      .filter((movement) => movement.purse_id === purseId)
      .reduce((total, movement) => total + Number(movement.amount), 0)
  )
}

/**
 * `balance_available` still subtracts the hold this payment is placing, so a
 * payment that already reserved money on a wallet reads back as if that money
 * were gone. Adding the payment's own hold back gives the amount the applicant
 * may actually allocate to it, which is the same rule the API applies.
 */
export function getAllocatableBalance(
  purse: PurseWithBalance,
  purseMovements: PaymentPurseMovement[]
) {
  return roundToTwoDecimals(
    purse.balance_available + getHoldForThisPayment(purse.id, purseMovements)
  )
}

/**
 * Returns the wallets with `balance_available` restated as what this payment can
 * allocate. Feeding these to the allocation form keeps what we display, what we
 * let the applicant pick and what the API accepts in agreement.
 */
export function applyPaymentHoldToPurses<T extends PurseWithBalance>(
  purses: T[],
  purseMovements: PaymentPurseMovement[]
): T[] {
  if (purseMovements.length === 0) {
    return purses
  }

  return purses.map((purse) => ({
    ...purse,
    balance_available: getAllocatableBalance(purse, purseMovements),
  }))
}

/**
 * Turns an existing allocation into the form's selected wallet shape so the
 * picker opens pre-filled with what the payment already reserved.
 *
 * The entries are flagged as manually edited on purpose: the applicant chose
 * these amounts when the payment was first accepted, so selecting another wallet
 * must not silently redistribute them away.
 */
export function buildSelectedPursesFromAllocation(
  purses: PurseWithBalance[],
  purseMovements: PaymentPurseMovement[]
): SelectedPurse[] {
  return aggregatePurseMovements(purseMovements).flatMap((entry) => {
    const purse = purses.find((p) => p.id === entry.purse_id)

    if (!purse) {
      return []
    }

    return [
      {
        purse_id: entry.purse_id,
        amount: entry.amount,
        name: purse.name,
        isSelected: true,
        hasBeenManuallyEdited: true,
      },
    ]
  })
}

/**
 * True when the applicant left the pre-filled allocation exactly as it was, in
 * which case we omit `purse_payments` and the API keeps the reservation rows it
 * already has instead of recreating them.
 */
export function isSameAllocation(
  current: PurseAllocationEntry[],
  existing: PaymentPurseMovement[]
) {
  const aggregatedExisting = aggregatePurseMovements(existing)
  const aggregatedCurrent = aggregatePurseMovements(current)

  if (aggregatedCurrent.length !== aggregatedExisting.length) {
    return false
  }

  return aggregatedExisting.every((entry) =>
    aggregatedCurrent.some(
      (currentEntry) =>
        currentEntry.purse_id === entry.purse_id &&
        currentEntry.amount === entry.amount
    )
  )
}
